/**
 * loadModal — generic on-demand modal loader.
 *
 * Fetches a modal's HTML from a WordPress AJAX action, injects it into
 * <body> once, and returns a Promise that resolves with the jQuery element.
 * Subsequent calls with the same action return the cached Promise.
 *
 * Usage:
 *   import { loadModal } from '../actions/loadModal';
 *
 *   loadModal(growtype_chat_ajax.get_use_credits_modal_action)
 *       .then(function ($modal) { window.growtypeModal?.show($modal[0]); })
 *       .catch(function (err) { console.error(err); });
 *
 *   // Or with a callback for quick use:
 *   loadModal(action, function ($modal) { window.growtypeModal?.show($modal[0]); });
 */

// Cache: action string → Promise<jQuery>
const _modalCache = {};
const {
    markPromiseHandled,
    runPromiseCallback,
} = require('../helpers/promiseSafety.cjs');

function isDuplicateJqueryScript(element) {
    if (!element || element.nodeName !== 'SCRIPT') return false;

    const src = element.getAttribute('src') || '';
    return /\/jquery(?:\.min)?\.js(?:[?#]|$)|\/jquery-migrate(?:\.min)?\.js(?:[?#]|$)/i.test(src);
}

export function parseModalHtml(html) {
    // Preserve legitimate inline modal behavior while preventing an AJAX
    // response from replacing the page's established jQuery runtime.
    const $nodes = $(jQuery.parseHTML(String(html || ''), document, true));

    $nodes.find('script[src]').filter(function () {
        return isDuplicateJqueryScript(this);
    }).remove();

    return $nodes.filter(function () {
        return this.nodeType === 1 && !isDuplicateJqueryScript(this);
    });
}

/**
 * @param {string}        action   WordPress AJAX action name.
 * @param {Function}      [cb]     Optional callback(jQuery $modal) called on success.
 * @param {Object}        [opts]
 * @param {string}        [opts.modalId]       If supplied, first checks whether the modal
 *                                              is already in the DOM by this ID and resolves
 *                                              immediately without an AJAX call.
 * @param {string}        [opts.modalSelector] CSS selector fallback when the modal has no id.
 * @param {boolean}       [opts.replace]       Replace existing DOM element instead of skip.
 * @param {Object}        [opts.ajaxData]      Extra POST params merged with { action }.
 * @returns {Promise<jQuery>}
 */
export function loadModal(action, cb, opts = {}) {
    if (!action) {
        const err = new Error('[loadModal] action is required');
        console.error(err);
        return markPromiseHandled(Promise.reject(err));
    }

    // If the modal is already in the DOM, skip the network request.
    if (opts.modalId && !opts.replace) {
        const $existing = $('#' + opts.modalId);
        if ($existing.length) {
            const p = Promise.resolve($existing);
            runPromiseCallback(p, cb);
            return p;
        }
    }

    if (opts.modalSelector && !opts.replace) {
        const $existing = $(opts.modalSelector);
        if ($existing.length) {
            const p = Promise.resolve($existing);
            runPromiseCallback(p, cb);
            return p;
        }
    }

    // Build a cache key that is unique per (action + ajaxData) combination
    // so that calls with different session tokens are cached separately.
    const cacheKey = opts.ajaxData
        ? action + '|' + JSON.stringify(opts.ajaxData)
        : action;

    // Return the cached promise for this action (only one request per action).
    if (_modalCache[cacheKey]) {
        runPromiseCallback(_modalCache[cacheKey], cb);
        return _modalCache[cacheKey];
    }

    _modalCache[cacheKey] = new Promise(function (resolve, reject) {
        const postData = Object.assign(
            { action: action, nonce: growtype_chat_ajax.nonce },
            opts.ajaxData || {}
        );
        $.ajax({
            url: growtype_chat_ajax.url,
            method: 'POST',
            dataType: 'json',
            data: postData,
            success: function (res) {
                if (!res || !res.success || !res.data || !res.data.html) {
                    const err = new Error('[loadModal] Server returned no HTML for action: ' + action);
                    delete _modalCache[cacheKey]; // allow retry on next call
                    reject(err);
                    return;
                }

                const html = res.data.html.trim();
                // Filter to element nodes only — PHP templates may have leading
                // whitespace/newlines that jQuery parses into text/comment nodes.
                // Without this, $el[0] can be a Comment node, crashing Bootstrap's
                // getOrCreateInstance which calls Object.keys(element.dataset).
                const $el = parseModalHtml(html);

                if (!$el.length) {
                    const err = new Error('[loadModal] Parsed HTML contains no element nodes for action: ' + action);
                    delete _modalCache[cacheKey];
                    reject(err);
                    return;
                }

                if (opts.modalId && opts.replace) {
                    const $old = $('#' + opts.modalId);
                    if ($old.length) {
                        $old.replaceWith($el);
                    } else {
                        $('body').append($el);
                    }
                } else {
                    $('body').append($el);
                }

                resolve($el);
            },
            error: function (jqXHR, textStatus) {
                const err = new Error('[loadModal] AJAX failed (' + textStatus + ') for action: ' + action);
                delete _modalCache[cacheKey]; // allow retry
                reject(err);
            },
        });
    });

    markPromiseHandled(_modalCache[cacheKey]);
    runPromiseCallback(_modalCache[cacheKey], cb);
    return _modalCache[cacheKey];
}

/**
 * Convenience: ensure a modal is in the DOM, then show it.
 *
 * @param {string}   action    WordPress AJAX action name.
 * @param {string}   modalId   ID of the modal element (without #).
 * @param {Function} [onReady] Called with ($modal) just before show().
 * @returns {Promise<jQuery>}
 */
export function loadAndShowModal(action, modalId, onReady) {
    const showPromise = loadModal(action, null, { modalId })
        .then(function ($modal) {
            if (typeof onReady === 'function') onReady($modal);

            // Use getElementById for a guaranteed element node — $modal[0] may
            // be a text node if the template has leading whitespace.
            const el = document.getElementById(modalId);
            if (!el) return $modal;

            window.growtypeModal?.show(el);

            return $modal;
        });

    return markPromiseHandled(showPromise);
}
